/**
 * InstanceList - Lista de instâncias do tenant
 * 
 * ARQUITETURA:
 * - Instância = Motor de mensagens (pode ser vinculado a uma inbox)
 * - Exibe todas as instâncias com status, provider e inbox vinculada
 * - Permite vincular/desvincular instâncias de inboxes
 * - Permite cancelar assinatura individual de instância (Fase 2)
 * - Permite comprar nova instância via addon-checkout (Fase 2)
 */
import { forwardRef, useImperativeHandle, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Loader2, 
  Server, 
  CheckCircle, 
  Clock, 
  Package,
  AlertCircle,
  AlertTriangle,
  Plus,
  XCircle,
  BarChart3,
  Crown
} from 'lucide-react';
import { useInstances, type Instance } from '@/hooks/useInstances';
import { useResourceLimits } from '@/hooks/useResourceLimits';
import { useTenantPlansViewMode } from '@/hooks/tenant/useTenantPlansV2';
import { InstanceListRow } from './InstanceListRow';
import { LinkInstanceDialog } from './LinkInstanceDialog';
import { CancelInstanceDialog } from './CancelInstanceDialog';
import { DeleteInstanceDialog } from './DeleteInstanceDialog';
import { ReactivateInstanceDialog } from './ReactivateInstanceDialog';
import { MigrateProviderDialog } from './MigrateProviderDialog';
import { InstanceDetailsModal } from './InstanceDetailsModal';
import { AddInstanceDialog } from './AddInstanceDialog';
import { InstanceKpisStrip } from './InstanceKpisStrip';
// Cota por provider (contratado/em uso/disponível) — auditoria dos KPIs
import { useTenantInstanceQuotaByProvider } from '@/hooks/useTenantInstanceQuotaByProvider';
import { cn } from '@/lib/utils';

/**
 * Props do componente InstanceList
 */
interface InstanceListProps {
  tenantId: string;
  className?: string;
}

/**
 * Handle imperativo exposto via ref para que o componente pai (Inboxes.tsx)
 * possa disparar o fluxo "Adicionar Instância" a partir de um botão renderizado
 * fora desta lista (ex.: na linha das abas).
 */
export interface InstanceListHandle {
  triggerAdd: () => void;
}

/**
 * Componente de métricas para KPI cards de instâncias
 */
interface MetricCardProps {
  title: string;
  value: string | number;
  icon: React.ElementType;
  variant: 'default' | 'success' | 'warning' | 'info' | 'destructive';
  subtitle?: string;
}

/** Estilos por variante - padrão horizontal KPI cards */
const variantStyles = {
  default: { cardBg: 'bg-purple-100 dark:bg-purple-900/30', iconColor: 'text-purple-700 dark:text-purple-300' },
  success: { cardBg: 'bg-lime-100 dark:bg-green-900/30', iconColor: 'text-green-700 dark:text-green-300' },
  warning: { cardBg: 'bg-cyan-100 dark:bg-cyan-900/30', iconColor: 'text-cyan-700 dark:text-cyan-300' },
  info: { cardBg: 'bg-blue-100 dark:bg-blue-900/30', iconColor: 'text-blue-700 dark:text-blue-300' },
  destructive: { cardBg: 'bg-pink-100 dark:bg-pink-900/30', iconColor: 'text-pink-700 dark:text-pink-300' },
};

/** Card de métrica horizontal - padrão unificado KPI */
const MetricCard = ({ title, value, icon: Icon, variant, subtitle }: MetricCardProps) => (
  <Card className={cn('border-0 shadow-none transition-all hover:shadow-md', variantStyles[variant].cardBg)}>
    <CardContent className="p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Icon className={cn('h-5 w-5 shrink-0', variantStyles[variant].iconColor)} />
          <p className="text-xs font-medium truncate text-gray-900 dark:text-gray-100">{title}</p>
        </div>
        <p className="text-xl font-bold leading-tight shrink-0 text-gray-900 dark:text-gray-100">{value}</p>
      </div>
      {subtitle && (
        <p className="text-[10px] mt-1 truncate text-muted-foreground ml-7">{subtitle}</p>
      )}
    </CardContent>
  </Card>
);

/**
 * Lista de instâncias do tenant
 */
export const InstanceList = forwardRef<InstanceListHandle, InstanceListProps>(
  function InstanceList({ tenantId, className }, ref) {
  const navigate = useNavigate();
  const {
    instances,
    isLoading,
    stats,
    unlinkInstance,
    isUnlinking,
    deleteInstance,
    reactivateInstance,
    isReactivating,
    refetch,
  } = useInstances(tenantId);

  // Hook de limites efetivos (plano base + add-ons) para KPI e alertas.
  // `effective_max_instagram_instances` é exposto pela RPC v1.12.0 mas ainda
  // não está tipado na interface — fazemos cast pontual aqui.
  const { limits: resourceLimits, isLoadingLimits } = useResourceLimits();
  const maxInstances = resourceLimits?.effective_max_instances ?? 0;
  const maxInstagramInstances =
    (resourceLimits as unknown as { effective_max_instagram_instances?: number } | null)
      ?.effective_max_instagram_instances ?? 0;
  const isEnterprise = resourceLimits?.is_enterprise ?? false;
  // Instâncias ativas = total - canceladas - suspensas
  const activeInstanceCount = stats.total - stats.cancelled - stats.suspended;
  const isLimitReached = !isEnterprise && activeInstanceCount >= maxInstances && maxInstances > 0;

  // Cota por provider (fonte única de verdade de "contratado"), usada tanto
  // nos cards por canal quanto no bloco de auditoria por provider.
  const { data: providerQuotas = [] } = useTenantInstanceQuotaByProvider(tenantId);

  // Agregações por canal para os KPIs.
  // Agora separamos WhatsApp (não oficial) de WhatsApp Oficial (WABA).
  const normalizeChannel = (ct?: string) => (ct ?? 'whatsapp').toLowerCase();

  /** Conta instâncias do tenant por status dentro de um conjunto de canais. */
  const countByChannel = (channels: string[], status: Instance['status']) =>
    instances.filter(
      (i) => i.status === status && channels.includes(normalizeChannel(i.channel_type)),
    ).length;

  /**
   * Métricas de um canal usando a RPC de cota como FONTE ÚNICA DE VERDADE.
   *
   * Motivo (correção 2026-08-20): antes o `limit` vinha da RPC, mas `inUse` e
   * `available` eram contados localmente na lista de instâncias. Como a vaga do
   * plano é compartilhada entre providers do mesmo canal (ex.: uma instância
   * WAHA `source_type='plan'` consome a vaga do pool que também serviria ao
   * WABA), o card exibia "13/14" sugerindo 1 vaga livre enquanto o modal de
   * nova inbox — corretamente — dizia que não havia instância/vaga disponível.
   *
   * Agora `inUse` e `available` também vêm da RPC. A contagem local permanece
   * apenas como fallback quando não há linha de cota para o canal.
   */
  const poolForChannel = (channels: string[], fallbackLimit: number) => {
    const rows = providerQuotas.filter((r) => channels.includes(normalizeChannel(r.channel_type)));

    // Sem cota por provider: mantém o comportamento legado (limite do plano
    // + contagem local de instâncias).
    if (rows.length === 0) {
      return {
        limit: fallbackLimit,
        available: countByChannel(channels, 'released'),
        inUse: countByChannel(channels, 'in_use'),
      };
    }

    // Ilimitado: usa o fallback como teto visual e a contagem local.
    if (rows.some((r) => r.allowed_qty < 0)) {
      return {
        limit: fallbackLimit,
        available: countByChannel(channels, 'released'),
        inUse: countByChannel(channels, 'in_use'),
      };
    }

    // Providers que compartilham a vaga do plano repetem o mesmo allowed_qty:
    // a linha com o maior "contratado" representa o teto do canal, e é dela
    // que também extraímos "em uso" e "disponível" (mesma base de cálculo).
    const top = rows.reduce((acc, r) => (r.allowed_qty > acc.allowed_qty ? r : acc), rows[0]);

    return {
      limit: top.allowed_qty,
      available: top.available_qty,
      inUse: top.in_use_qty,
    };
  };

  const WA_CHANNELS = ['whatsapp'];
  const WA_OFICIAL_CHANNELS = ['whatsapp_oficial'];
  const IG_CHANNELS = ['instagram'];

  // Fallback do canal WhatsApp: limite do plano quando não houver cota por provider.
  const whatsappPool = poolForChannel(WA_CHANNELS, maxInstances);
  const whatsappOfficialPool = poolForChannel(WA_OFICIAL_CHANNELS, 0);
  const instagramPool = poolForChannel(IG_CHANNELS, maxInstagramInstances);


  // Estado para dialog de vincular instância
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [instanceToLink, setInstanceToLink] = useState<Instance | null>(null);

  // Estado para dialog de cancelar instância
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [instanceToCancel, setInstanceToCancel] = useState<Instance | null>(null);

  // Estado para dialog de exclusão definitiva de instância
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [instanceToDelete, setInstanceToDelete] = useState<Instance | null>(null);

  // Reativar instância cancelada (v1.6.0 — credenciais/inbox preservadas)
  const [reactivateDialogOpen, setReactivateDialogOpen] = useState(false);
  const [instanceToReactivate, setInstanceToReactivate] = useState<Instance | null>(null);

  // Estado para dialog de migrar provider
  const [migrateDialogOpen, setMigrateDialogOpen] = useState(false);
  const [instanceToMigrate, setInstanceToMigrate] = useState<Instance | null>(null);

  // Estado para modal de detalhes/auditoria da instância
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [instanceForDetails, setInstanceForDetails] = useState<Instance | null>(null);

  // v1.5.0: detecta planos V1 — habilita o modal de criação manual de instância.
  // Em planos legados (Stripe), mantém o redirect histórico para /tenant/plans.
  const { data: viewMode } = useTenantPlansViewMode();
  const isV1 = viewMode === 'integracao_v1';
  const [addInstanceOpen, setAddInstanceOpen] = useState(false);



  // Handler - Abre o modal de detalhes
  const handleViewDetails = (instance: Instance) => {
    setInstanceForDetails(instance);
    setDetailsOpen(true);
  };

  // Handlers - Vincular
  const handleLinkToInbox = (instance: Instance) => {
    setInstanceToLink(instance);
    setLinkDialogOpen(true);
  };

  const handleUnlink = async (instance: Instance) => {
    if (!instance.inbox_id) return;
    await unlinkInstance(instance.inbox_id);
  };

  const handleLinkSuccess = () => {
    setLinkDialogOpen(false);
    setInstanceToLink(null);
  };

  // Handlers - Cancelar
  const handleCancelInstance = (instance: Instance) => {
    setInstanceToCancel(instance);
    setCancelDialogOpen(true);
  };

  const handleCancelSuccess = () => {
    setCancelDialogOpen(false);
    setInstanceToCancel(null);
    // Refetch redundante para garantir atualização imediata da lista
    refetch();
  };

  // Handlers - Excluir instância (definitivo)
  const handleDeleteInstance = (instance: Instance) => {
    setInstanceToDelete(instance);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async (instance: Instance) => {
    await deleteInstance(instance.id);
    setInstanceToDelete(null);
    // Refetch redundante para garantir atualização imediata da lista
    refetch();
  };

  // Handlers - Reativar instância cancelada (v1.6.0 — credenciais preservadas)
  const handleReactivateInstance = (instance: Instance) => {
    setInstanceToReactivate(instance);
    setReactivateDialogOpen(true);
  };

  const handleConfirmReactivate = async (instance: Instance) => {
    await reactivateInstance(instance.id);
    setInstanceToReactivate(null);
    // Refetch redundante para garantir atualização imediata da lista
    refetch();
  };

  // Handlers - Migrar Provider
  const handleMigrateProvider = (instance: Instance) => {
    setInstanceToMigrate(instance);
    setMigrateDialogOpen(true);
  };

  const handleMigrateSuccess = () => {
    setMigrateDialogOpen(false);
    setInstanceToMigrate(null);
  };

  // Instâncias released disponíveis para migração
  const releasedInstances = instances.filter(i => i.status === 'released');
  const hasReleasedInstances = releasedInstances.length > 0;

  // Handler - Comprar/criar nova instância.
  // V1: abre o modal de seleção de provider (cota controlada via add-ons V1).
  // Legado: mantém o redirect histórico para a página de planos.
  const handleBuyInstance = () => {
    if (isV1) {
      setAddInstanceOpen(true);
      return;
    }
    navigate('/tenant/plans');
  };

  // Expõe o handler para o componente pai disparar o fluxo "Adicionar Instância"
  // a partir de um botão renderizado fora desta lista (linha das abas).
  useImperativeHandle(ref, () => ({ triggerAdd: handleBuyInstance }), [isV1]);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className={cn("space-y-4 sm:space-y-6", className)}>
      {/* KPI Cards — segmentação por canal (WhatsApp x Instagram) +
          status transversais (Pendente, Restritas).
          O botão "Adicionar Instância" foi movido para a linha das abas
          (em Inboxes.tsx) via ref imperativa. */}
      <InstanceKpisStrip
        whatsapp={whatsappPool}
        whatsappOfficial={whatsappOfficialPool}
        instagram={instagramPool}
        providerQuotas={providerQuotas}
        pending={stats.pending}
        suspended={stats.suspended}
        cancelled={stats.cancelled}
      />

      {/* Alerta de instâncias suspensas por pagamento atrasado */}
      {stats.suspended > 0 && (
        <Card className="relative overflow-hidden border-0 bg-gradient-to-r from-orange-50 via-amber-50 to-orange-50 dark:from-orange-950/30 dark:via-amber-950/30 dark:to-orange-950/30">
          <div className="absolute inset-0 bg-gradient-to-r from-orange-500/5 via-amber-500/10 to-orange-500/5" />
          <CardContent className="relative py-4 px-4 sm:py-5 sm:px-6 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
            <div className="relative flex-shrink-0">
              <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-orange-500/20 to-amber-500/20 blur-lg" />
              <div className="relative flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 shadow-lg shadow-orange-500/20">
                <AlertTriangle className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-sm sm:text-base text-orange-700 dark:text-orange-300">
                {stats.suspended} {stats.suspended === 1 ? 'instância suspensa' : 'instâncias suspensas'}
              </h3>
              <p className="text-xs sm:text-sm text-muted-foreground">
                <span className="hidden sm:inline">Pagamento atrasado detectado. Regularize o pagamento para reativar as instâncias.</span>
                <span className="sm:hidden">Regularize o pagamento para reativar.</span>
              </p>
            </div>
            <Button 
              size="sm" 
              className="w-full sm:w-auto bg-gradient-to-r from-orange-500 to-amber-500 text-white border-0 shadow-md shadow-orange-500/20 hover:shadow-lg hover:shadow-orange-500/30 hover:scale-[1.02] active:scale-[0.98] transition-all"
              onClick={() => navigate('/tenant/plans')}
            >
              <Crown className="mr-1.5 h-4 w-4" />
              <span className="hidden sm:inline">Regularizar</span>
              <span className="sm:hidden">Regularizar</span>
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Alerta de limite de instâncias atingido */}
      {isLimitReached && (
        <Card className="relative overflow-hidden border-0 bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 dark:from-amber-950/30 dark:via-orange-950/30 dark:to-amber-950/30">
          <div className="absolute inset-0 bg-gradient-to-r from-amber-500/5 via-orange-500/10 to-amber-500/5" />
          <CardContent className="relative py-4 px-4 sm:py-5 sm:px-6 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
            <div className="relative flex-shrink-0">
              <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 blur-lg" />
              <div className="relative flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 shadow-lg shadow-amber-500/20">
                <AlertCircle className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <h3 className="font-semibold text-sm sm:text-base text-amber-700 dark:text-amber-300">
                  Limite de instâncias atingido
                </h3>
                <Badge className="bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20 text-[10px] sm:text-xs">
                  {activeInstanceCount}/{maxInstances}
                </Badge>
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground">
                <span className="hidden sm:inline">Faça upgrade do seu plano ou contrate um add-on para expandir suas instâncias.</span>
                <span className="sm:hidden">Faça upgrade para mais instâncias.</span>
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0 w-full sm:w-auto">
              <Button 
                variant="outline" 
                size="sm" 
                className="flex-1 sm:flex-none border-amber-500/30 text-amber-600 hover:bg-amber-500/10 hover:border-amber-500/50 transition-all"
                onClick={() => navigate('/tenant/plans')}
              >
                <Package className="mr-1.5 h-4 w-4" />
                Add-on
              </Button>
              <Button 
                size="sm" 
                className="flex-1 sm:flex-none bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0 shadow-md shadow-amber-500/20 hover:shadow-lg hover:shadow-amber-500/30 hover:scale-[1.02] active:scale-[0.98] transition-all"
                onClick={() => navigate('/tenant/plans')}
              >
                <Crown className="mr-1.5 h-4 w-4" />
                <span className="hidden sm:inline">Ver Planos</span>
                <span className="sm:hidden">Upgrade</span>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Totalizador — movido para abaixo dos KPIs/banner de limite. */}
      <div className="flex items-center">
        <p className="text-xs sm:text-sm text-muted-foreground">
          {instances.length} {instances.length === 1 ? 'instância' : 'instâncias'}
        </p>
      </div>

      {/* Lista de Instâncias */}
      {instances.length === 0 ? (
        <Card className="relative overflow-hidden rounded-2xl border-2 border-dashed border-muted-foreground/20 bg-gradient-to-br from-muted/30 to-muted/10">
          <CardContent className="p-6 sm:p-12">
            <div className="flex flex-col items-center justify-center text-center">
              <div className="w-14 h-14 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center mb-4 sm:mb-6">
                <Server className="h-7 w-7 sm:h-10 sm:w-10 text-primary/60" />
              </div>
              
              <h3 className="text-lg sm:text-xl font-semibold text-foreground mb-2">
                Nenhuma instância disponível
              </h3>
              <p className="text-sm sm:text-base text-muted-foreground mb-4 sm:mb-6 max-w-md">
                Instâncias são criadas automaticamente ao contratar um plano ou add-on.
              </p>
              <Button 
                size="sm" 
                onClick={handleBuyInstance}
                className="transition-all hover:shadow-md"
              >
                <Plus className="mr-1.5 h-4 w-4" />
                Contratar Instância
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            {/* Header da tabela */}
            <div className="hidden sm:flex items-center gap-4 px-4 py-2 border-b bg-muted/30 text-xs font-medium text-muted-foreground">
              <div className="w-8 shrink-0" /> {/* Ícone */}
              <div className="flex-1 min-w-0">Instância</div>
              <div className="hidden sm:block w-24 shrink-0">Cód. Instância</div>
              <div className="hidden sm:block w-32 shrink-0">Provider</div>
              <div className="hidden md:block w-44 shrink-0">Inbox Vinculada</div>
              <div className="hidden lg:block w-24 shrink-0">Origem</div>
              <div className="hidden xl:block w-28 shrink-0">Assinatura</div>
              <div className="w-28 shrink-0">Status</div>
              <div className="w-8 shrink-0" /> {/* Ações */}
            </div>

            {/* Lista de instâncias */}
            <div className="divide-y divide-border">
              {instances.map((instance) => (
                <InstanceListRow
                  key={instance.id}
                  instance={instance}
                  onLinkToInbox={handleLinkToInbox}
                  onUnlink={handleUnlink}
                  onViewDetails={handleViewDetails}
                  onCancelInstance={handleCancelInstance}
                  onDeleteInstance={handleDeleteInstance}
                  onReactivate={handleReactivateInstance}
                  onMigrateProvider={handleMigrateProvider}
                  hasReleasedInstances={hasReleasedInstances}
                  isUnlinking={isUnlinking}
                  isReactivating={isReactivating}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Legenda de status */}
      {instances.length > 0 && (
        <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
          <span className="font-medium">Legenda:</span>
          <div className="flex items-center gap-1.5">
            <Badge variant="outline" className="text-[10px] bg-green-500/10 text-green-600 border-green-500/20">
              <CheckCircle className="h-3 w-3 mr-1" />
              Disponível
            </Badge>
            <span>= Pronta para vincular a uma inbox</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Badge variant="outline" className="text-[10px] bg-blue-500/10 text-blue-600 border-blue-500/20">
              <Package className="h-3 w-3 mr-1" />
              Em Uso
            </Badge>
            <span>= Já vinculada a uma inbox</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Badge variant="outline" className="text-[10px] bg-orange-500/10 text-orange-600 border-orange-500/20">
              <AlertTriangle className="h-3 w-3 mr-1" />
              Suspensa
            </Badge>
            <span>= Pagamento atrasado</span>
          </div>
        </div>
      )}

      {/* v1.5.0: Modal de criação manual de instância (V1) */}
      <AddInstanceDialog
        tenantId={tenantId}
        open={addInstanceOpen}
        onOpenChange={setAddInstanceOpen}
      />

      {/* Dialog de vincular instância */}
      <LinkInstanceDialog
        open={linkDialogOpen}
        onOpenChange={setLinkDialogOpen}
        instance={instanceToLink}
        tenantId={tenantId}
        onSuccess={handleLinkSuccess}
      />

      {/* Dialog de cancelar instância */}
      {/* Dialog de exclusão definitiva da instância */}
      <DeleteInstanceDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        instance={instanceToDelete}
        onConfirm={handleConfirmDelete}
      />

      {/* Dialog de reativação de instância cancelada */}
      <ReactivateInstanceDialog
        open={reactivateDialogOpen}
        onOpenChange={setReactivateDialogOpen}
        instance={instanceToReactivate}
        onConfirm={handleConfirmReactivate}
      />

      <CancelInstanceDialog
        open={cancelDialogOpen}
        onOpenChange={setCancelDialogOpen}
        instance={instanceToCancel}
        tenantId={tenantId}
        onSuccess={handleCancelSuccess}
      />

      {/* Dialog de migrar provider */}
      <MigrateProviderDialog
        open={migrateDialogOpen}
        onOpenChange={setMigrateDialogOpen}
        sourceInstance={instanceToMigrate}
        releasedInstances={releasedInstances}
        tenantId={tenantId}
        onSuccess={handleMigrateSuccess}
      />

      {/* Modal de detalhes/auditoria da instância */}
      <InstanceDetailsModal
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
        instance={instanceForDetails}
        tenantId={tenantId}
      />
    </div>
  );
});